import { Arc, Circle, Group, Line, Rect, Text } from "react-konva";
import { BOARD_WIDTH, boardDimensions, type FieldKind, type Orientation } from "./scene.js";

/**
 * Pitch background — pure Konva, no image assets.
 *
 * Four variants:
 *   - `pitch_green`: classic green field with white markings (full pitch)
 *   - `pitch_blank`: white surface with dark markings
 *   - `pitch_zones`: white surface with a 6×6 tactical grid overlay
 *   - `pitch_half`: only the attacking half (top half in landscape)
 *
 * All four are drawn in the board's logical coordinate space so the Stage
 * transform takes care of fitting them to the viewport.
 */

interface Props {
  kind: FieldKind;
  orientation: Orientation;
}

const COLORS = {
  grass: "#1f6e3a",
  grassAlt: "#22773f",
  paper: "#ffffff",
  ink: "rgba(15,18,22,0.85)",
  inkSoft: "rgba(15,18,22,0.35)",
  zoneTint: "rgba(15,18,22,0.04)",
  line: "rgba(255,255,255,0.85)",
};

export function Pitch({ kind, orientation }: Props) {
  const { width, height } = boardDimensions(orientation, kind);
  const isPaper = kind === "pitch_blank" || kind === "pitch_zones";
  const bg = isPaper ? COLORS.paper : COLORS.grass;
  const lineColor = isPaper ? COLORS.ink : COLORS.line;
  const half = kind === "pitch_half";
  const isLandscape = width >= height;
  // Rounded corners on the pitch surface itself — matches the overall
  // rounded-card aesthetic of the rest of the UI. The stripes / zones /
  // markings inside still draw to full bounds; a group-level clipFunc
  // crops them to the rounded outline so nothing leaks past the curve.
  const cornerRadius = Math.min(width, height) * 0.025;
  const clipRounded = (
    ctx:
      | CanvasRenderingContext2D
      | {
          beginPath: () => void;
          moveTo: (x: number, y: number) => void;
          lineTo: (x: number, y: number) => void;
          quadraticCurveTo: (cx: number, cy: number, x: number, y: number) => void;
          closePath: () => void;
        },
  ) => {
    const r = cornerRadius;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(width - r, 0);
    ctx.quadraticCurveTo(width, 0, width, r);
    ctx.lineTo(width, height - r);
    ctx.quadraticCurveTo(width, height, width - r, height);
    ctx.lineTo(r, height);
    ctx.quadraticCurveTo(0, height, 0, height - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
  };

  return (
    <Group listening={false} clipFunc={clipRounded}>
      {/* Surface */}
      <Rect x={0} y={0} width={width} height={height} fill={bg} cornerRadius={cornerRadius} />

      {/* Mowing stripes for green pitches only.
          Streifen verlaufen IMMER parallel zur kurzen Seite (wie im
          Querformat) und werden entlang der langen Achse unterteilt.
          Anzahl: 10 beim Vollfeld, 5 beim Halbfeld. */}
      {!isPaper && (
        <Group>
          {(() => {
            const stripes = half ? 5 : 10;
            const longLen = isLandscape ? width : height;
            const step = longLen / stripes;
            return Array.from({ length: stripes }).map((_, i) => {
              const fill = i % 2 === 0 ? COLORS.grass : COLORS.grassAlt;
              if (isLandscape) {
                return (
                  <Rect
                    key={i}
                    x={i * step}
                    y={0}
                    width={step}
                    height={height}
                    fill={fill}
                    opacity={0.55}
                  />
                );
              }
              return (
                <Rect
                  key={i}
                  x={0}
                  y={i * step}
                  width={width}
                  height={step}
                  fill={fill}
                  opacity={0.55}
                />
              );
            });
          })()}
        </Group>
      )}

      {/* Zones overlay: 6×6 grid + labels */}
      {kind === "pitch_zones" && (
        <ZonesGrid width={width} height={height} isLandscape={isLandscape} />
      )}

      {/* Pitch markings */}
      <PitchMarkings width={width} height={height} lineColor={lineColor} half={half} />
    </Group>
  );
}

/* ─── zones grid (6×6 with A–F / 1–6 labels) ───────────────────────── */

function ZonesGrid({
  width,
  height,
  isLandscape,
}: {
  width: number;
  height: number;
  isLandscape: boolean;
}) {
  const margin = 20;
  // Labels sit JUST inside the outer rectangle so they don't overlap the
  // border line (which is centred on `margin` with a 4px stroke).
  const tickInset = margin + 6;

  // Zone boundaries follow the actual pitch markings so the grid lines
  // coincide with the penalty box, the 5m goal area and the centre line.
  //
  // Letters A..F slice the SHORT axis (perpendicular to the long side):
  //   A / F  – touchline → penalty-area edge
  //   B / E  – penalty-area edge → goal-area edge
  //   C / D  – each half of the goal area
  //
  // Numbers 1..6 slice the LONG axis:
  //   1 / 6  – goal line → penalty-area front edge
  //   2 / 5  – penalty-area front → half-way of that field half
  //   3 / 4  – half-way of that field half → centre line
  //
  // Top-left cell is always A1 (in both orientations).
  const longSide = isLandscape ? width : height;
  const shortSide = isLandscape ? height : width;
  const SCALE = 1.25; // keep in sync with PitchMarkings
  const penaltyDepth = Math.round(longSide * 0.115 * SCALE);
  const penaltyWidth = Math.round(shortSide * 0.42 * SCALE);
  const goalWidth = Math.round(shortSide * 0.18 * SCALE);

  // Boundaries along the SHORT axis (A..F), 7 values:
  const shortCenter = shortSide / 2;
  const shortBounds: readonly [number, number, number, number, number, number, number] = [
    margin,
    shortCenter - penaltyWidth / 2,
    shortCenter - goalWidth / 2,
    shortCenter,
    shortCenter + goalWidth / 2,
    shortCenter + penaltyWidth / 2,
    shortSide - margin,
  ];

  // Boundaries along the LONG axis (1..6), 7 values:
  const longLeftPenalty = margin + penaltyDepth;
  const longRightPenalty = longSide - margin - penaltyDepth;
  const longCenter = longSide / 2;
  const longBounds: readonly [number, number, number, number, number, number, number] = [
    margin,
    longLeftPenalty,
    (longLeftPenalty + longCenter) / 2,
    longCenter,
    (longCenter + longRightPenalty) / 2,
    longRightPenalty,
    longSide - margin,
  ];

  // Map A..F / 1..6 onto X/Y based on orientation.
  // Landscape: long axis = X (numbers), short axis = Y (letters).
  // Portrait : long axis = Y (numbers), short axis = X (letters).
  const xBounds = isLandscape ? longBounds : shortBounds;
  const yBounds = isLandscape ? shortBounds : longBounds;
  // Helpers — tuples are fixed length 7, but indexing by a number variable
  // narrows to `T[number] | undefined` under `noUncheckedIndexedAccess`.
  const xb = (i: number): number => xBounds[i] as number;
  const yb = (i: number): number => yBounds[i] as number;

  const numbers = ["1", "2", "3", "4", "5", "6"];
  const letters = ["A", "B", "C", "D", "E", "F"];
  const xLabels = isLandscape ? numbers : letters;
  const yLabels = isLandscape ? letters : numbers;

  // Pick a label size that fits the smallest cell on either axis.
  let smallestSpan = Infinity;
  for (let i = 0; i < 6; i++) {
    const dx = xb(i + 1) - xb(i);
    const dy = yb(i + 1) - yb(i);
    smallestSpan = Math.min(smallestSpan, dx, dy);
  }
  const labelFont = Math.max(10, Math.round(smallestSpan * 0.45));

  return (
    <Group listening={false}>
      {/* Checkerboard-like tint */}
      {Array.from({ length: 6 }).map((_, r) =>
        Array.from({ length: 6 }).map((__, c) => {
          const tinted = (r + c) % 2 === 1;
          if (!tinted) return null;
          return (
            <Rect
              key={`z-${r}-${c}`}
              x={xb(c)}
              y={yb(r)}
              width={xb(c + 1) - xb(c)}
              height={yb(r + 1) - yb(r)}
              fill={COLORS.zoneTint}
            />
          );
        }),
      )}

      {/* Vertical dashed grid lines (5 interior lines between 6 columns) */}
      {Array.from({ length: 5 }).map((_, i) => {
        const x = xb(i + 1);
        return (
          <Line
            key={`gv-${i}`}
            points={[x, margin, x, height - margin]}
            stroke={COLORS.inkSoft}
            strokeWidth={1.5}
            dash={[6, 6]}
          />
        );
      })}
      {/* Horizontal dashed grid lines */}
      {Array.from({ length: 5 }).map((_, i) => {
        const y = yb(i + 1);
        return (
          <Line
            key={`gh-${i}`}
            points={[margin, y, width - margin, y]}
            stroke={COLORS.inkSoft}
            strokeWidth={1.5}
            dash={[6, 6]}
          />
        );
      })}

      {/* Column labels (top + bottom edges) */}
      {xLabels.map((label, i) => {
        const centerX = (xb(i) + xb(i + 1)) / 2;
        return (
          <Group key={`lx-${i}`}>
            <Text
              x={centerX - labelFont}
              y={tickInset}
              width={labelFont * 2}
              height={labelFont}
              text={label}
              fontSize={labelFont}
              fontStyle="500"
              fill={COLORS.inkSoft}
              align="center"
              verticalAlign="middle"
            />
            <Text
              x={centerX - labelFont}
              y={height - tickInset - labelFont}
              width={labelFont * 2}
              height={labelFont}
              text={label}
              fontSize={labelFont}
              fontStyle="500"
              fill={COLORS.inkSoft}
              align="center"
              verticalAlign="middle"
            />
          </Group>
        );
      })}

      {/* Row labels (left + right edges) */}
      {yLabels.map((label, i) => {
        const centerY = (yb(i) + yb(i + 1)) / 2;
        return (
          <Group key={`ly-${i}`}>
            <Text
              x={tickInset}
              y={centerY - labelFont / 2}
              width={labelFont * 2}
              height={labelFont}
              text={label}
              fontSize={labelFont}
              fontStyle="500"
              fill={COLORS.inkSoft}
              align="center"
              verticalAlign="middle"
            />
            <Text
              x={width - tickInset - labelFont * 2}
              y={centerY - labelFont / 2}
              width={labelFont * 2}
              height={labelFont}
              text={label}
              fontSize={labelFont}
              fontStyle="500"
              fill={COLORS.inkSoft}
              align="center"
              verticalAlign="middle"
            />
          </Group>
        );
      })}
    </Group>
  );
}

/* ─── markings drawn in board coords ─────────────────────────────────
 * Landscape: goals at left/right, center line vertical at width/2.
 * Portrait : goals at top/bottom, center line horizontal at height/2.
 * Half pitch: only one goal end + a halfway line on the open side. */

interface MarkingsProps {
  width: number;
  height: number;
  lineColor: string;
  half: boolean;
}

function PitchMarkings({ width, height, lineColor, half }: MarkingsProps) {
  const stroke = 4;
  const isLandscape = width >= height;

  // Markierungen werden relativ zur VOLLEN Spielfeld-Länge berechnet, damit
  // ein Halbfeld die echten realen Proportionen (Strafraum ~31% des Halbes,
  // statt ~16% wie auf einem ganzen Feld) zeigt. `longRef` ist also 1920
  // unabhängig von Orientation/Half.
  const longRef = BOARD_WIDTH;
  // Inner markings are scaled ~12% larger than the strict real-world
  // proportions so they read clearly on tablet/phone screens — the outer
  // rectangle (and margin) stay untouched.
  const SCALE = 1.25;
  // Penalty box: ~22% of long side deep, ~40% wide (scaled).
  const penaltyDepth = Math.round(longRef * 0.115 * SCALE);
  const penaltyWidth = Math.round((isLandscape ? height : width) * 0.42 * SCALE);
  const goalDepth = Math.round(longRef * 0.042 * SCALE);
  const goalWidth = Math.round((isLandscape ? height : width) * 0.18 * SCALE);
  const arcRadius = Math.round(longRef * 0.063 * SCALE);
  const centerRadius = Math.round(longRef * 0.063 * SCALE);
  const spotRadius = 6;
  const margin = 20;

  // Penalty arc geometry: the arc is centered on the penalty spot and the
  // visible portion is the part that sits OUTSIDE the penalty box. We derive
  // the visible sweep from the chord that lies exactly on the box's inner
  // edge so the arc meets the box flush on both sides.
  const spotOffset = Math.round(penaltyDepth * 0.65);
  const spotToBoxEdge = penaltyDepth - spotOffset;
  const halfAngleDeg = Math.min(
    89,
    (Math.acos(Math.min(1, spotToBoxEdge / arcRadius)) * 180) / Math.PI,
  );
  const arcSweep = halfAngleDeg * 2;

  // Convenience for centered axis perpendicular to the long side.
  const perpCenter = isLandscape ? height / 2 : width / 2;

  return (
    <Group listening={false}>
      {/* Outer rectangle (clipped to half if requested) */}
      <Rect
        x={margin}
        y={margin}
        width={width - margin * 2}
        height={height - margin * 2}
        stroke={lineColor}
        strokeWidth={stroke}
      />

      {!half && (
        <>
          {/* Center line */}
          {isLandscape ? (
            <Line
              points={[width / 2, margin, width / 2, height - margin]}
              stroke={lineColor}
              strokeWidth={stroke}
            />
          ) : (
            <Line
              points={[margin, height / 2, width - margin, height / 2]}
              stroke={lineColor}
              strokeWidth={stroke}
            />
          )}
          {/* Center circle + spot */}
          <Circle
            x={width / 2}
            y={height / 2}
            radius={centerRadius}
            stroke={lineColor}
            strokeWidth={stroke}
          />
          <Circle x={width / 2} y={height / 2} radius={spotRadius} fill={lineColor} />
        </>
      )}

      {isLandscape ? (
        <>
          {/* Right (attacking) end — always shown */}
          <Rect
            x={width - margin - penaltyDepth}
            y={perpCenter - penaltyWidth / 2}
            width={penaltyDepth}
            height={penaltyWidth}
            stroke={lineColor}
            strokeWidth={stroke}
          />
          <Rect
            x={width - margin - goalDepth}
            y={perpCenter - goalWidth / 2}
            width={goalDepth}
            height={goalWidth}
            stroke={lineColor}
            strokeWidth={stroke}
          />
          <Circle
            x={width - margin - Math.round(penaltyDepth * 0.65)}
            y={perpCenter}
            radius={spotRadius}
            fill={lineColor}
          />
          <Arc
            x={width - margin - spotOffset}
            y={perpCenter}
            innerRadius={arcRadius - stroke / 2}
            outerRadius={arcRadius + stroke / 2}
            angle={arcSweep}
            rotation={180 - halfAngleDeg}
            fill={lineColor}
          />
          {/* Left end only on full pitch */}
          {!half && (
            <>
              <Rect
                x={margin}
                y={perpCenter - penaltyWidth / 2}
                width={penaltyDepth}
                height={penaltyWidth}
                stroke={lineColor}
                strokeWidth={stroke}
              />
              <Rect
                x={margin}
                y={perpCenter - goalWidth / 2}
                width={goalDepth}
                height={goalWidth}
                stroke={lineColor}
                strokeWidth={stroke}
              />
              <Circle
                x={margin + Math.round(penaltyDepth * 0.65)}
                y={perpCenter}
                radius={spotRadius}
                fill={lineColor}
              />
              <Arc
                x={margin + spotOffset}
                y={perpCenter}
                innerRadius={arcRadius - stroke / 2}
                outerRadius={arcRadius + stroke / 2}
                angle={arcSweep}
                rotation={-halfAngleDeg}
                fill={lineColor}
              />
            </>
          )}
        </>
      ) : (
        <>
          {/* Top (attacking) end — always shown */}
          <Rect
            x={perpCenter - penaltyWidth / 2}
            y={margin}
            width={penaltyWidth}
            height={penaltyDepth}
            stroke={lineColor}
            strokeWidth={stroke}
          />
          <Rect
            x={perpCenter - goalWidth / 2}
            y={margin}
            width={goalWidth}
            height={goalDepth}
            stroke={lineColor}
            strokeWidth={stroke}
          />
          <Circle
            x={perpCenter}
            y={margin + Math.round(penaltyDepth * 0.65)}
            radius={spotRadius}
            fill={lineColor}
          />
          <Arc
            x={perpCenter}
            y={margin + spotOffset}
            innerRadius={arcRadius - stroke / 2}
            outerRadius={arcRadius + stroke / 2}
            angle={arcSweep}
            rotation={90 - halfAngleDeg}
            fill={lineColor}
          />
          {/* Bottom end only on full pitch */}
          {!half && (
            <>
              <Rect
                x={perpCenter - penaltyWidth / 2}
                y={height - margin - penaltyDepth}
                width={penaltyWidth}
                height={penaltyDepth}
                stroke={lineColor}
                strokeWidth={stroke}
              />
              <Rect
                x={perpCenter - goalWidth / 2}
                y={height - margin - goalDepth}
                width={goalWidth}
                height={goalDepth}
                stroke={lineColor}
                strokeWidth={stroke}
              />
              <Circle
                x={perpCenter}
                y={height - margin - Math.round(penaltyDepth * 0.65)}
                radius={spotRadius}
                fill={lineColor}
              />
              <Arc
                x={perpCenter}
                y={height - margin - spotOffset}
                innerRadius={arcRadius - stroke / 2}
                outerRadius={arcRadius + stroke / 2}
                angle={arcSweep}
                rotation={270 - halfAngleDeg}
                fill={lineColor}
              />
            </>
          )}
        </>
      )}

      {/* Halfway line on the open side of a half pitch */}
      {half && (
        <>
          {isLandscape ? (
            <>
              <Line
                points={[margin, perpCenter, margin, perpCenter]}
                stroke={lineColor}
                strokeWidth={stroke}
              />
              <Line
                points={[margin, margin, margin, height - margin]}
                stroke={lineColor}
                strokeWidth={stroke}
              />
              <Arc
                x={margin}
                y={perpCenter}
                innerRadius={centerRadius - stroke / 2}
                outerRadius={centerRadius + stroke / 2}
                angle={180}
                rotation={-90}
                fill={lineColor}
              />
              <Circle x={margin} y={perpCenter} radius={spotRadius} fill={lineColor} />
            </>
          ) : (
            <>
              <Line
                points={[margin, height - margin, width - margin, height - margin]}
                stroke={lineColor}
                strokeWidth={stroke}
              />
              <Arc
                x={width / 2}
                y={height - margin}
                innerRadius={centerRadius - stroke / 2}
                outerRadius={centerRadius + stroke / 2}
                angle={180}
                rotation={180}
                fill={lineColor}
              />
              <Circle x={width / 2} y={height - margin} radius={spotRadius} fill={lineColor} />
            </>
          )}
        </>
      )}
    </Group>
  );
}
